import {
  clearAppDataCache,
  clearPendingAppDataRequest,
  cloneAppData,
  getCachedAppData,
  getPendingAppDataRequest,
  setCachedAppData,
  setPendingAppDataRequest,
  updateCachedAppData,
} from "./appDataCache";
import {
  fetchCommonTournaments,
  deleteRegistroFromSupabase,
  deleteTournamentFromSupabase,
  fetchCompletePreferences,
  fetchRegistros,
  fetchTournaments,
  saveCompletePreferencesToSupabase,
  saveRegistroToSupabase,
  saveTournamentToSupabase,
} from "./completeRepository";
import {
  fetchSimplifiedPlatforms,
  fetchSimplifiedSettings,
  fetchSimplifiedSnapshotPlatforms,
  fetchSimplifiedSnapshots,
  mapSimplifiedBankrollFromDb,
  saveSimplifiedBankrollToSupabase,
} from "./simplifiedRepository";
import {
  mapCompletePreferencesFromDb,
  mapEntryFromDb,
  mapTournamentFromDb,
} from "./supabaseMapping";
import { isMissingCompletePreferencesError } from "./supabaseSchemaErrors";
import { normalizeRegistros, normalizeTournaments } from "./dataModel";

export const loadCommonTournaments = async () => {
  const result = await fetchCommonTournaments();
  if (result.error) throw result.error;

  return normalizeTournaments(
    (result.data || []).map((row) =>
      mapTournamentFromDb(row, { source: "common" })
    )
  );
};

const fetchAppData = async (userId) => {
  const [
    tournamentsResult,
    commonTournamentsResult,
    registrosResult,
    completePreferencesResult,
    settingsResult,
    platformsResult,
    snapshotsResult,
  ] = await Promise.all([
    fetchTournaments(userId),
    fetchCommonTournaments(),
    fetchRegistros(userId),
    fetchCompletePreferences(userId),
    fetchSimplifiedSettings(userId),
    fetchSimplifiedPlatforms(userId),
    fetchSimplifiedSnapshots(userId),
  ]);

  const firstError = [
    tournamentsResult,
    commonTournamentsResult,
    registrosResult,
    isMissingCompletePreferencesError(completePreferencesResult.error)
      ? { error: null }
      : completePreferencesResult,
    settingsResult,
    platformsResult,
    snapshotsResult,
  ].find((result) => result.error)?.error;

  if (firstError) throw firstError;

  const snapshotPlatformsResult = await fetchSimplifiedSnapshotPlatforms(
    (snapshotsResult.data || []).map((snapshot) => snapshot.snapshot_id)
  );

  if (snapshotPlatformsResult.error) throw snapshotPlatformsResult.error;

  return {
    tournaments: normalizeTournaments(
      (tournamentsResult.data || []).map((row) =>
        mapTournamentFromDb(row, { source: "user" })
      )
    ),
    commonTournaments: normalizeTournaments(
      (commonTournamentsResult.data || []).map((row) =>
        mapTournamentFromDb(row, { source: "common" })
      )
    ),
    registros: normalizeRegistros((registrosResult.data || []).map(mapEntryFromDb)),
    completePreferences: mapCompletePreferencesFromDb(completePreferencesResult.data),
    simplifiedBankroll: mapSimplifiedBankrollFromDb({
      settings: settingsResult.data,
      platforms: platformsResult.data || [],
      snapshots: snapshotsResult.data || [],
      snapshotPlatforms: snapshotPlatformsResult.data || [],
    }),
  };
};

export const loadAppData = async (userId, options = {}) => {
  const { force = false } = options;
  if (!userId) return cloneAppData({});

  const cached = getCachedAppData(userId);
  if (!force && cached) return cached;

  const pending = getPendingAppDataRequest(userId);
  if (!force && pending) return pending.then(cloneAppData);

  const request = fetchAppData(userId)
    .then((data) => {
      setCachedAppData(userId, data);
      return cloneAppData(data);
    })
    .finally(() => {
      clearPendingAppDataRequest(userId);
    });

  setPendingAppDataRequest(userId, request);
  return request;
};

export {
  clearAppDataCache,
  deleteRegistroFromSupabase,
  deleteTournamentFromSupabase,
  getCachedAppData,
  saveCompletePreferencesToSupabase,
  saveRegistroToSupabase,
  saveSimplifiedBankrollToSupabase,
  saveTournamentToSupabase,
  setCachedAppData,
  updateCachedAppData,
};
